/**
 * EmailSettings — Tier 2 OAuth "send from rep mailbox" scaffold.
 *
 * Allows BDR / AE to connect their Gmail or Outlook 365 mailbox so AIP
 * can send cadence emails on their behalf (OAuth Tier 2 send flow).
 *
 * Connection state: localStorage key aip:email-integration:v1
 * Server endpoints: /api/oauth/gmail/start · /api/oauth/outlook/start
 *   — scaffolded only; real tokens require Google Cloud + Azure app registration.
 *
 * OUTLOOK_OAUTH_ENABLED: set VITE_OUTLOOK_OAUTH_ENABLED=true to show the
 * Outlook card. Currently disabled — the Azure OAuth flow is not yet wired.
 */

import { useState, useEffect, useCallback } from "react";
import OperatorShell from "@/components/operator/OperatorShell";

// Feature flag — false until Azure app registration is complete
const OUTLOOK_OAUTH_ENABLED = import.meta.env.VITE_OUTLOOK_OAUTH_ENABLED === "true";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProviderState {
  connected: boolean;
  email: string | null;
  connected_at: string | null;
}

interface IntegrationState {
  gmail: ProviderState;
  outlook: ProviderState;
}

const STORAGE_KEY = "aip:email-integration:v1";

const DEFAULT_STATE: IntegrationState = {
  gmail:   { connected: false, email: null, connected_at: null },
  outlook: { connected: false, email: null, connected_at: null },
};

function loadIntegration(): IntegrationState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULT_STATE, ...JSON.parse(raw) } : DEFAULT_STATE;
  } catch {
    return DEFAULT_STATE;
  }
}

function saveIntegration(state: IntegrationState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface StatusBadgeProps {
  connected: boolean;
  email: string | null;
}

function StatusBadge({ connected, email }: StatusBadgeProps) {
  if (connected && email) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
        style={{ background: "#DCFCE7", color: "#166534" }}
      >
        <span
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{ background: "#16A34A" }}
        />
        Connected as {email}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ background: "#EFEFEE", color: "#6B6B70" }}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-neutral-300 flex-shrink-0" />
      Not connected
    </span>
  );
}

// Google "G" monogram placeholder
function GoogleLogo() {
  return (
    <div
      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 font-black text-white text-lg shadow-sm"
      style={{ background: "linear-gradient(135deg, #0B0B0F, #34A853, #FBBC05, #EA4335)", backgroundSize: "200% 200%" }}
      aria-label="Google"
    >
      G
    </div>
  );
}

// Microsoft "M" monogram placeholder
function MicrosoftLogo() {
  return (
    <div
      className="w-10 h-10 rounded-lg flex-shrink-0 grid grid-cols-2 gap-0.5 p-1.5 shadow-sm"
      style={{ background: "#EFEFEE" }}
      aria-label="Microsoft"
    >
      <div className="rounded-sm" style={{ background: "#F25022" }} />
      <div className="rounded-sm" style={{ background: "#7FBA00" }} />
      <div className="rounded-sm" style={{ background: "#0B0B0F" }} />
      <div className="rounded-sm" style={{ background: "#FFB900" }} />
    </div>
  );
}

// ─── Provider card ────────────────────────────────────────────────────────────

interface ProviderCardProps {
  logo: React.ReactNode;
  name: string;
  description: string;
  state: ProviderState;
  onConnect: () => void;
  onDisconnect: () => void;
  connecting: boolean;
}

function ProviderCard({
  logo,
  name,
  description,
  state,
  onConnect,
  onDisconnect,
  connecting,
}: ProviderCardProps) {
  return (
    <div
      className="flex-1 min-w-0 bg-white rounded-xl p-6 shadow-sm border"
      style={{ borderColor: "#E5E5E7" }}
    >
      <div className="flex items-start gap-4 mb-4">
        {logo}
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-neutral-800 mb-0.5">{name}</div>
          <div className="text-xs text-neutral-500">{description}</div>
        </div>
      </div>

      <div className="mb-5">
        <StatusBadge connected={state.connected} email={state.email} />
        {state.connected && state.connected_at && (
          <div className="mt-2 text-[11px] text-neutral-500">
            Connected {new Date(state.connected_at).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })}
          </div>
        )}
      </div>

      {state.connected ? (
        <button
          onClick={onDisconnect}
          className="w-full px-4 py-2 rounded-lg text-sm font-semibold border transition-colors hover:bg-red-50"
          style={{ borderColor: "#DC2626", color: "#DC2626" }}
        >
          Disconnect
        </button>
      ) : (
        <button
          onClick={onConnect}
          disabled={connecting}
          className="w-full px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-60"
          style={{ background: "#0B0B0F" }}
        >
          {connecting ? "Redirecting..." : `Connect ${name}`}
        </button>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function EmailSettings() {
  const [integration, setIntegration] = useState<IntegrationState>(loadIntegration);
  const [banner, setBanner] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<"gmail" | "outlook" | null>(null);
  const [outlookComingSoon, setOutlookComingSoon] = useState(false);

  // Handle OAuth callback: ?connected=gmail|outlook
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    if (connected === "gmail" || connected === "outlook") {
      const updated = { ...loadIntegration() };
      updated[connected] = {
        connected: true,
        email: `you@${connected === "gmail" ? "gmail.com" : "outlook.com"}`,
        connected_at: new Date().toISOString(),
      };
      saveIntegration(updated);
      setIntegration(updated);
      // Clear the query param without reload
      window.history.replaceState({}, "", window.location.pathname + window.location.hash);
    }
  }, []);

  const handleConnect = useCallback(async (provider: "gmail" | "outlook") => {
    setConnecting(provider);
    setBanner(null);
    try {
      const endpoint = provider === "gmail"
        ? "/api/oauth/gmail/start"
        : "/api/oauth/outlook/start";
      const res = await fetch(endpoint);
      const data = await res.json();

      if (!data.configured) {
        setBanner(data.message ?? "OAuth not yet configured.");
        return;
      }
      if (data.auth_url) {
        window.location.href = data.auth_url;
      }
    } catch {
      setBanner("Could not reach the server. Check your connection.");
    } finally {
      setConnecting(null);
    }
  }, []);

  const handleDisconnect = useCallback((provider: "gmail" | "outlook") => {
    const updated = { ...integration };
    updated[provider] = { connected: false, email: null, connected_at: null };
    saveIntegration(updated);
    setIntegration(updated);
    setBanner(null);
  }, [integration]);

  return (
    <OperatorShell
      breadcrumbs={[{ label: "Settings" }, { label: "Email Integration" }]}
      pageTitle="Email Integration"
      pageMeta="Connect your mailbox for cadence send"
    >
      <div className="max-w-3xl space-y-8">

        {/* Header */}
        <div>
          <div
            className="text-[10px] uppercase tracking-[0.28em] font-bold mb-2"
            style={{ color: "#DC2626" }}
          >
            Settings — Email Integration
          </div>
          <h1
            className="text-2xl font-bold mb-1"
            style={{ color: "#0B0B0F" }}
          >
            Email Integration
          </h1>
          <p className="text-neutral-500 text-sm">
            Connect your mailbox so Attilius can send cadence emails on your behalf.
          </p>
        </div>

        {/* Placeholder / error banner */}
        {banner && (
          <div
            className="px-4 py-3 rounded-lg border text-sm leading-relaxed"
            style={{
              background: "#FFF7ED",
              borderColor: "#FED7AA",
              color: "#92400E",
            }}
          >
            <span className="font-semibold">OAuth not yet configured</span> — placeholder mode.
            Bryan needs to register a Google Cloud project and Microsoft Azure app to enable real send.
            <div className="mt-1 text-xs opacity-75">{banner}</div>
          </div>
        )}

        {/* Provider cards */}
        <div className="flex flex-col sm:flex-row gap-4">
          <ProviderCard
            logo={<GoogleLogo />}
            name="Gmail"
            description="Send via Gmail using OAuth"
            state={integration.gmail}
            onConnect={() => handleConnect("gmail")}
            onDisconnect={() => handleDisconnect("gmail")}
            connecting={connecting === "gmail"}
          />
          {OUTLOOK_OAUTH_ENABLED ? (
            <ProviderCard
              logo={<MicrosoftLogo />}
              name="Outlook"
              description="Send via Outlook 365 using OAuth"
              state={integration.outlook}
              onConnect={() => handleConnect("outlook")}
              onDisconnect={() => handleDisconnect("outlook")}
              connecting={connecting === "outlook"}
            />
          ) : (
            <div
              className="flex-1 min-w-0 bg-white rounded-xl p-6 shadow-sm border"
              style={{ borderColor: "#E5E5E7" }}
            >
              <div className="flex items-start gap-4 mb-4">
                <MicrosoftLogo />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-neutral-800 mb-0.5">Outlook</div>
                  <div className="text-xs text-neutral-500">Send via Outlook 365 using OAuth</div>
                </div>
              </div>
              <div className="mb-5">
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                  style={{ background: "#EFEFEE", color: "#6B6B70" }}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-neutral-300 flex-shrink-0" />
                  Coming soon
                </span>
              </div>
              <button
                disabled
                className="w-full px-4 py-2 rounded-lg text-sm font-semibold border transition-colors cursor-not-allowed opacity-40"
                style={{ borderColor: "#C9C9CC", color: "#6B6B70" }}
                onClick={() => setOutlookComingSoon(true)}
              >
                Connect Outlook
              </button>
              {outlookComingSoon && (
                <p className="mt-2 text-xs text-neutral-500 text-center">
                  Outlook integration is coming soon.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Explanation panels */}
        <div
          className="rounded-xl border bg-white p-6 space-y-5"
          style={{ borderColor: "#E5E5E7" }}
        >
          <div>
            <div
              className="text-xs font-bold uppercase tracking-[0.18em] mb-3"
              style={{ color: "#0B0B0F" }}
            >
              What happens when you connect
            </div>
            <ul className="space-y-2">
              {[
                "Attilius sends emails through your account",
                "Emails appear in your Sent folder",
                "Replies thread back into the prospect record",
                "You stay in control — disconnect anytime",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-neutral-600">
                  <span
                    className="w-4 h-4 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center text-white font-bold text-[10px]"
                    style={{ background: "#0B0B0F" }}
                  >
                    &#10003;
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div
            className="h-px"
            style={{ background: "#E5E5E7" }}
          />

          <div>
            <div
              className="text-xs font-bold uppercase tracking-[0.18em] mb-3"
              style={{ color: "#6B6B70" }}
            >
              What we do not do
            </div>
            <ul className="space-y-2">
              {[
                "We never read your inbox without your permission",
                "We never send without your approval",
                "You see every message before it goes out",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-neutral-500">
                  <span
                    className="w-4 h-4 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center font-bold text-[10px]"
                    style={{ background: "#EFEFEE", color: "#8E8E94" }}
                  >
                    &#215;
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

      </div>
    </OperatorShell>
  );
}
